import {
  emptyStrategicInitiativeData,
  StrategicInitiativeData,
  StrategicInitiativePillar,
} from "@/common/strategic-initiatives.ts";
import DeleteStrategicInitiativeDialog from "@/components/strategic-initiative/DeleteStrategicInitiativeDialog.tsx";
import StrategicInitiativeFilters from "@/components/strategic-initiative/StrategicInitiativeFilters.tsx";
import StrategicInitiativeSearchBar from "@/components/strategic-initiative/StrategicInitiativeSearchBar.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Slider } from "@/components/ui/slider.tsx";
import { Toast } from "@/components/ui/toast.tsx";
import {
  createStrategicInitiative,
  deleteStrategicInitiative,
  getStrategicInitiatives,
  updateStrategicInitiative,
} from "@/lib/serverComm.tsx";
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const StrategicInitiatives: React.FC = () => {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastTitle, setToastTitle] = useState("");
  const [toastDescription, setToastDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [strategicInitiatives, setStrategicInitiatives] = useState<
    StrategicInitiativeData[] | null
  >(null);
  const [strategicInitiativePillars, setStrategicInitiativePillars] = useState<
    StrategicInitiativePillar[]
  >([]);
  const [filteredStrategicInitiatives, setFilteredStrategicInitiatives] =
    useState<StrategicInitiativeData[] | null>(null);
  const [strategicInitiativeEditable, setStrategicInitiativeEditable] =
    useState<StrategicInitiativeData | null>(null);
  const [
    activeStrategicInitiativePillars,
    setActiveStrategicInitiativePillars,
  ] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isLoading || strategicInitiatives !== null) {
      return;
    }

    reload().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, strategicInitiatives]);

  useEffect(() => {
    // if nothing to filter --> exit
    if (strategicInitiatives === null) {
      return;
    }

    // if empty search --> reset to full-list
    if (
      activeStrategicInitiativePillars.length === 0 &&
      searchQuery.length === 0
    ) {
      setFilteredStrategicInitiatives(strategicInitiatives);
      return;
    }

    const query = searchQuery.toLowerCase();

    // otherwise we filter
    const filteredStrategicInitiatives = strategicInitiatives.filter((item) => {
      if (item.pillar === undefined) {
        return false; // some data is corrupted, filter out the whole item
      }

      let foundByPillar = true;
      let foundBySearchQuery = true;

      // filter for pillars
      if (activeStrategicInitiativePillars.length > 0) {
        foundByPillar = activeStrategicInitiativePillars.includes(item.pillar);
      }

      // filter for a query on `description`
      if (query.length > 0) {
        if (item.description !== undefined) {
          foundBySearchQuery = item.description.toLowerCase().includes(query);
        }
      }

      // both flags must be true (we are not partially filtering)
      return foundByPillar && foundBySearchQuery;
    });

    setFilteredStrategicInitiatives(filteredStrategicInitiatives);
  }, [strategicInitiatives, activeStrategicInitiativePillars, searchQuery]);

  const onSaveChanges = async () => {
    if (strategicInitiativeEditable) {
      const request = {
        pillar: strategicInitiativeEditable.pillar,
        description: strategicInitiativeEditable.description,
        threshold: strategicInitiativeEditable.threshold,
      };

      if (strategicInitiativeEditable.rootId) {
        await updateStrategicInitiative(
          strategicInitiativeEditable.rootId,
          request,
        );
      } else {
        await createStrategicInitiative(request);
      }

      reload().then();
      closeEditModal();
      setIsToastOpen(true);
      setToastTitle("Changes were saved.");
      setToastDescription("Changes will take effect from the next update.");
    }
  };

  const handlePillarChange = (newPillar: string) => {
    if (!strategicInitiativeEditable) {
      return;
    }

    strategicInitiativeEditable.pillar = newPillar;
    setStrategicInitiativeEditable({ ...strategicInitiativeEditable });
  };

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!strategicInitiativeEditable) {
      return;
    }

    strategicInitiativeEditable.threshold = Number(e.target.value);
    setStrategicInitiativeEditable({ ...strategicInitiativeEditable });
  };

  const handleThresholdCommit = (value: number[]) => {
    if (!strategicInitiativeEditable || value.length === 0) {
      return;
    }

    strategicInitiativeEditable.threshold = value[0];
    setStrategicInitiativeEditable({ ...strategicInitiativeEditable });
  };

  const openEditModal = (
    data: StrategicInitiativeData | undefined = undefined,
  ) => {
    setStrategicInitiativeEditable(
      data === undefined ? emptyStrategicInitiativeData() : { ...data },
    );
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (prompt: StrategicInitiativeData) => {
    setStrategicInitiativeEditable({ ...prompt }); // clone object for editing
    setIsDeleteModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setStrategicInitiativeEditable(null);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setStrategicInitiativeEditable(null);
  };

  const removeStrategicInitiative = async () => {
    if (isDeleting) {
      return;
    }

    if (strategicInitiativeEditable !== null) {
      setIsDeleting(true);
      await deleteStrategicInitiative(strategicInitiativeEditable.rootId);
      setIsDeleting(false);

      reload().then();

      setIsToastOpen(true);
      setToastTitle("Strategic initiative was remove.");
      setToastDescription("Changes will take effect from the next update.");
    }

    closeDeleteModal();
  };

  const reload = async () => {
    setIsLoading(true);

    const response = await getStrategicInitiatives();

    if (response === null) {
      setStrategicInitiativePillars([]);
      setStrategicInitiatives([]);
      setIsLoading(false);
    } else {
      setStrategicInitiatives(handlePillars([...response]));
      setIsLoading(false);
    }
  };

  const handlePillars = (response: StrategicInitiativeData[]) => {
    // some data might be corrupted
    const data = response.filter((item) => item.id !== undefined);

    const pillars: StrategicInitiativePillar[] = Array.from(
      new Map(
        data.map((item) => {
          if (item.pillar === undefined) {
            return ["REMOVE", null];
          }

          const pillar = item.pillar;
          const title = item.pillar
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());

          // set title for main-set
          item.title = title;

          return [pillar, { pillar, title, rootId: item.rootId }]; // map by pillar to remove duplicates
        }),
      ).values(),
    ).filter((item) => item !== null);

    if (strategicInitiativePillars.length === 0) {
      setStrategicInitiativePillars(pillars);
    }

    return data;
  };

  return (
    <ScrollArea className="h-full">
      <Toast
        open={isToastOpen}
        setOpen={setIsToastOpen}
        title={toastTitle}
        description={toastDescription}
      />
      <div className="p-8">
        <div className="flex items-center w-full gap-4">
          <Link to={`/deep-insights`}>
            <ArrowLeft
              size={24}
              className="rounded-full text-sky-500 bg-white p-1"
            />
          </Link>
          <h1 className="text-sky-500 text-2xl font-bold">
            Strategic Initiatives
          </h1>
        </div>

        <p>This page is intended for Parita administration only.</p>

        <div className="flex items-center w-full gap-4 mt-6 mb-3">
          <StrategicInitiativeSearchBar
            className="mx-auto"
            onInput={setSearchQuery}
            isLoading={isLoading}
          ></StrategicInitiativeSearchBar>
          <div className="flex-1"></div>
          <Button
            className="bg-sky-500 rounded-full pl-12 pr-12 justify-central"
            onClick={() => openEditModal()}
          >
            <span className="flex-1">Add Strategic Initiative</span>
          </Button>
        </div>

        <StrategicInitiativeFilters
          strategicInitiativePillars={strategicInitiativePillars}
          onChange={setActiveStrategicInitiativePillars}
        ></StrategicInitiativeFilters>

        {isLoading || filteredStrategicInitiatives === null ? (
          <p className="mt-4">Loading...</p>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {filteredStrategicInitiatives.map((data) => (
              <div
                key={data.rootId}
                className="flex-1 border-b border-b-gray-300"
              >
                <div className="flex items-start justify-items-start w-full mb-4 gap-4">
                  <div className="flex flex-col flex-1 w-full gap-1">
                    <h1 className="text-l font-bold">{data.title}</h1>
                    <p>{data.description}</p>
                  </div>

                  <div className="flex gap-2 pt-8">
                    <Button
                      className="bg-sky-500 rounded-full pl-12 pr-12 justify-central"
                      onClick={() => openEditModal(data)}
                    >
                      <span className="flex-1">Edit</span>
                    </Button>
                    <Button
                      className="bg-sky-500 rounded-full pl-12 pr-12 justify-central"
                      onClick={() => openDeleteModal(data)}
                    >
                      <span className="flex-1">Remove</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {strategicInitiativeEditable && (
        <Dialog open={isEditModalOpen} onOpenChange={closeEditModal}>
          <DialogContent className="w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {strategicInitiativeEditable.title ??
                  "Add Strategic Initiative"}
              </DialogTitle>
            </DialogHeader>

            <p className="block text-xs text-black font-medium -mb-2">Pillar</p>
            <Select
              name="pillar"
              defaultValue={strategicInitiativeEditable.pillar}
              onValueChange={handlePillarChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a dataset" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {strategicInitiativePillars.map((pillar) => (
                    <SelectItem key={pillar.rootId} value={pillar.pillar}>
                      {pillar.title}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <label
              htmlFor="threshold"
              className="block text-xs text-black font-medium -mb-2"
            >
              Threshold
            </label>
            <div className="flex flex-row gap-4 items-center">
              <div className="w-1/10">
                <input
                  id="threshold"
                  type="number"
                  value={strategicInitiativeEditable.threshold}
                  name="threshold"
                  className="border border-gray-300 rounded-md"
                  step={0.01}
                  min={0}
                  max={1}
                  readOnly={true}
                  onInput={handleThresholdChange}
                />
              </div>
              <div className="flex-1">
                <Slider
                  min={0}
                  max={1}
                  step={0.01}
                  defaultValue={[strategicInitiativeEditable.threshold]}
                  onValueChange={handleThresholdCommit}
                />
              </div>
            </div>

            <label
              htmlFor="description"
              className="block text-xs text-black font-medium mb-2"
            >
              Content
            </label>
            <textarea
              id="description"
              name="description"
              defaultValue={strategicInitiativeEditable?.description ?? ""}
              onChange={(e) =>
                setStrategicInitiativeEditable((prev) =>
                  prev ? { ...prev, description: e.target.value } : prev,
                )
              }
              placeholder="Enter your prompt query here..."
              className="w-full h-[300px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            <p className="mt-4 text-xs font-bold">
              Changes will take effect from the next update.
            </p>

            <div className="flex items-center w-full gap-2">
              <div className="flex-1"></div>
              <Button
                className="bg-sky-100 text-black rounded-full pl-12 pr-12 justify-central"
                onClick={closeEditModal}
              >
                <span className="flex-1">Cancel</span>
              </Button>
              <Button
                className="bg-sky-500 rounded-full pl-12 pr-12 justify-central"
                onClick={onSaveChanges}
              >
                <span className="flex-1">Save Changes</span>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <DeleteStrategicInitiativeDialog
        isOpen={isDeleteModalOpen}
        isDeleting={isDeleting}
        close={closeDeleteModal}
        remove={removeStrategicInitiative}
      />
    </ScrollArea>
  );
};

export default StrategicInitiatives;
